import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Star, ArrowRight, Loader2 } from 'lucide-react';
import { useRecap, PeriodeTabs, Vide, dateFr } from './RecapCommun';

const RapportLigne = ({ r }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
    <p className="text-xs font-semibold text-slate-500">{dateFr(r.date)}{r.stagiaires.length > 0 && ` · ${r.stagiaires.join(', ')}`}</p>
    <div className="mt-1 flex flex-wrap gap-1">
      {Object.entries(r.items).flatMap(([cat, items]) => items.map(it => (
        <span key={`${cat}-${it}`} className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700" title={cat}>{it}</span>
      )))}
    </div>
    {r.texte_libre && <p className="mt-1.5 text-xs italic text-slate-600">« {r.texte_libre} »</p>}
  </div>
);

const FormateurCarte = ({ f }) => (
  <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`recap-formateur-${f.formateur_id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-semibold text-[#0F172A]">{f.nom}</p>
      <div className="flex gap-2 text-xs">
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-blue-700"><FileText className="inline h-3 w-3 mr-1" />{f.nb_rapports} rapport{f.nb_rapports > 1 ? 's' : ''}</span>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700"><Star className="inline h-3 w-3 mr-1" />{f.nb_notes} note{f.nb_notes > 1 ? 's' : ''}{f.moyenne_notes !== null && ` · moy. ${f.moyenne_notes}/10`}</span>
      </div>
    </div>
    {f.rapports.length > 0 && <div className="space-y-2">{f.rapports.map(r => <RapportLigne key={r.rapport_id} r={r} />)}</div>}
    {f.dernieres_notes.length > 0 && (
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
        {f.dernieres_notes.map((n, i) => (
          <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
            <span className="min-w-0 sm:truncate text-slate-600">{dateFr(n.date)} · <strong className="text-slate-800">{n.stagiaire}</strong> · {n.categorie} — {n.item}</span>
            <span className="shrink-0 font-bold text-[#0F172A]">{n.note}/{n.bareme}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const RecapFormateurs = () => {
  const [jours, setJours] = useState(7);
  const data = useRecap('/dashboard/recap-formateurs', jours);
  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5 space-y-4" data-testid="recap-formateurs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodeTabs value={jours} onChange={setJours} testId="recap-formateurs-periode" />
        <div className="flex gap-4 text-sm">
          <Link to="/rapport-journalier" className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline" data-testid="lien-tous-rapports">Tous les rapports <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/carnet-general" className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline" data-testid="lien-carnet-notes">Carnet de notes <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
      {!data ? <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        : data.erreur ? <Vide>Récap indisponible pour le moment.</Vide>
        : data.formateurs.length === 0 ? <Vide>Aucun rapport ni note sur cette période.</Vide>
        : <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">{data.formateurs.map(f => <FormateurCarte key={f.formateur_id} f={f} />)}</div>}
    </div>
  );
};
